import { useCallback, useEffect, useState, useMemo } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import ExportBar from "../components/ExportBar";

export default function LeaveDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState({ employee: "", cc: "", ccOwner: "", portfolio: "", year: "", status: "" });

  const load = useCallback(async () => {
    const [latest, hist] = await Promise.all([
      api.get("/leaves/latest"),
      api.get("/leaves/history/list"),
    ]);
    setData(latest.data);
    setHistory(hist.data.history || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const records = useMemo(() => {
    if (!data?.records) return [];
    const f = filter;
    return data.records.filter((r) => {
      if (f.employee && !(r.employee || "").toLowerCase().includes(f.employee.toLowerCase())) return false;
      if (f.cc && !(r.cc || "").toLowerCase().includes(f.cc.toLowerCase())) return false;
      if (f.ccOwner && !(r.ccOwner || "").toLowerCase().includes(f.ccOwner.toLowerCase())) return false;
      if (f.portfolio && !(r.portfolio || "").toLowerCase().includes(f.portfolio.toLowerCase())) return false;
      if (f.year && String(r.year || "") !== f.year) return false;
      if (f.status && r.status !== f.status) return false;
      return true;
    });
  }, [data, filter]);

  async function deleteUpload(id) {
    if (!confirm("Delete this leave upload?")) return;
    await api.delete(`/leaves/${id}`);
    load();
  }

  if (!data) return <div className="text-slate-500">Loading…</div>;
  if (data.empty) return <div className="bg-white p-8 rounded-xl text-center text-slate-500">No leave data yet. Upload a Leave Report Excel from the Upload tab.</div>;

  const mismatchCount = records.filter((r) => r.mismatch).length;

  return (
    <div data-testid="leaves-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold" style={{ color: "#1a3666" }}>Leave Dashboard</h2>
          <p className="text-sm text-slate-500 mt-0.5">{data.filename} · Year {data.year} · {records.length} of {data.records.length} shown · {mismatchCount} mismatches</p>
        </div>
        <ExportBar spec={() => ({
          title: "Leave Dashboard",
          subtitle: `Year ${data.year || "—"} · ${data.filename || ""}`,
          filters: filter,
          columns: [
            { key: "employee", label: "Employee" },
            { key: "cc", label: "CC" },
            { key: "ccOwner", label: "CC Owner" },
            { key: "portfolio", label: "Portfolio" },
            { key: "year", label: "Year" },
            { key: "status", label: "Status" },
            { key: "total", label: "Total" },
            { key: "planned", label: "Planned" },
            { key: "applied", label: "Applied" },
            { key: "approved", label: "Approved" },
            { key: "taken", label: "Taken" },
            { key: "available", label: "Available" },
            { key: "pending", label: "Pending (A−A)" },
            { key: "mismatch", label: "Mismatch?", get: (r) => r.mismatch ? "Yes" : "" },
          ],
          rows: records,
          filename: "vSDI_Leaves",
        })} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Employee</th>
                <th className="px-3 py-2 text-left">CC</th>
                <th className="px-3 py-2 text-left">CC Owner</th>
                <th className="px-3 py-2 text-left">Portfolio</th>
                <th className="px-3 py-2 text-center">Year</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-center">Total</th>
                <th className="px-3 py-2 text-center">Planned</th>
                <th className="px-3 py-2 text-center">Applied</th>
                <th className="px-3 py-2 text-center">Approved</th>
                <th className="px-3 py-2 text-center">Taken</th>
                <th className="px-3 py-2 text-center">Available</th>
              </tr>
              <tr className="bg-slate-100">
                {["employee", "cc", "ccOwner", "portfolio"].map((k) => (
                  <th key={k} className="px-2 py-1"><input data-testid={`lv-filter-${k}`} value={filter[k]}
                    onChange={(e) => setFilter((f) => ({ ...f, [k]: e.target.value }))}
                    placeholder="filter…" className="w-full px-1.5 py-1 text-xs border rounded" /></th>
                ))}
                <th className="px-2 py-1"><input value={filter.year}
                  onChange={(e) => setFilter((f) => ({ ...f, year: e.target.value }))}
                  placeholder="…" className="w-full px-1.5 py-1 text-xs border rounded" /></th>
                <th className="px-2 py-1"><input value={filter.status}
                  onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
                  placeholder="filter…" className="w-full px-1.5 py-1 text-xs border rounded" /></th>
                <th colSpan={6}></th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={`${r.employee}-${r.cc}-${r.year}`} data-testid="leave-row"
                    className={`border-t border-slate-100 hover:bg-slate-50 ${r.mismatch ? "bg-red-50 border-l-[3px] border-l-red-500" : ""}`}>
                  <td className="px-3 py-2 font-medium">{r.employee}</td>
                  <td className="px-3 py-2 text-xs">{r.cc}</td>
                  <td className="px-3 py-2 text-xs">{r.ccOwner}</td>
                  <td className="px-3 py-2 text-xs">{r.portfolio}</td>
                  <td className="px-3 py-2 text-center text-xs">{r.year}</td>
                  <td className="px-3 py-2 text-xs">{r.status}</td>
                  <td className="px-3 py-2 text-center">{r.total}</td>
                  <td className="px-3 py-2 text-center">{r.planned}</td>
                  <td className="px-3 py-2 text-center">{r.applied}</td>
                  <td className="px-3 py-2 text-center">{r.approved}</td>
                  <td className="px-3 py-2 text-center">{r.taken}</td>
                  <td className="px-3 py-2 text-center font-semibold">{r.available}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold" style={{ color: "#1a3666" }}>Leave Upload History</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Filename</th>
              <th className="px-3 py-2 text-left">Uploaded</th>
              <th className="px-3 py-2 text-center">Year</th>
              <th className="px-3 py-2 text-center">Records</th>
              {user?.role === "admin" && <th className="px-3 py-2 text-center">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{h.filename}</td>
                <td className="px-3 py-2 text-xs">{h.uploaded_at}</td>
                <td className="px-3 py-2 text-center">{h.year}</td>
                <td className="px-3 py-2 text-center">{h.record_count}</td>
                {user?.role === "admin" && (
                  <td className="px-3 py-2 text-center">
                    <button data-testid={`lv-delete-${h.id}`} onClick={() => deleteUpload(h.id)}
                            className="text-red-600 hover:underline text-xs">Delete</button>
                  </td>
                )}
              </tr>
            ))}
            {!history.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No uploads yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
